import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Register() {
  const [role, setRole] = useState("student");
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [grades, setGrades] = useState([]);
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [image, setImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [t, i18n] = useTranslation("global");

  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    birth: "",
    childCode: "",
    stage: "",
    grade: "",
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStages();
    fetchGrades();
  }, []);
  
  useEffect(() => {
    if (user.stage) {
      const filtered = grades.filter((grade) => grade.stage.id === Number(user.stage));
      setFilteredGrades(filtered);
    }
  }, [user.stage, grades]);
  
  const fetchStages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stages`, {
        headers: {
          'Accept-Language': i18n.resolvedLanguage || 'en',
        },
      });
      const data = await response.json();
      if (data?.data) {
        setStages(data.data);
      } else {
        toast.error("Failed to load stages");
      }
    } catch (err) {
      toast.error("Error fetching stages");
    }
  };
  
  const fetchGrades = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/grades`, {
        headers: {
          'Accept-Language': i18n.resolvedLanguage || 'en',
        },
      });
      const data = await response.json();
      if (data?.data) {
        setGrades(data.data);
      } else {
        toast.error("Failed to load grades");
      }
    } catch (err) {
      toast.error("Error fetching grades");
    }
  };
  
  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData();
    formData.append("name", user.name);
    formData.append("email", user.email);
    formData.append("type", role);
    formData.append("password", user.password);
    formData.append("birthdate", user.birth);
    formData.append("password_confirmation", user.confirmPassword);

    if (role === "student") {
      formData.append("grade_id", user.grade);
      formData.append("stage_id", user.stage);
    }
    if (role === "parent") {
      formData.append("code_student", user.childCode);
    }

    if (image) {
      formData.append("image", image);
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: "POST",
        headers: {
          "Accept-Language": i18n.language,
        },
        body: formData,
      });
      
      const data = await res.json();
      localStorage.setItem("user", JSON.stringify(data));

      if (!res.ok) {
        if (data.errors) {
          const firstError = Object.values(data.errors)[0][0];
          setError(firstError);
          toast.error(firstError);
        } else {
          const msg = data.message || "Something went wrong.";
          setError(msg);
          toast.error(msg);
        }
      } else {
        toast.success("Account created successfully!");
        toast.success("OTP sent successfully! Check your email.", {
          autoClose: 2000,
          onClose: () => {
            localStorage.setItem("otpAllowed", "true");
            navigate("/otp");
          }
        });
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong with OTP.", {
        autoClose: 2000,
      });
    }
  };

  return (
    <section style={{ backgroundColor: "#193C6E", minHeight: "100vh", padding: "30px 0" }}>
      <ToastContainer />
      <div className="container h-100">
        <div className="row d-flex justify-content-center align-items-center min-vh-100">
          <div className="col-lg-6 col-md-12">
            <img style={{ margin: "0" }} src="assets/imgs/Layer 3.png" width="100%" alt="" />
          </div>
          <div className="col-lg-6 col-md-12">
            <div className="card-singin p-4 shadow rounded-4">
              <div>
                <img src="assets/imgs/تميز copy 1.svg" alt="Taamayaz Logo" className="mb-3" />
                <h2 className="second-color ">{t("register.title")}</h2>
                <p className="paragraph-color ">{t("register.subtitle")}</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="name">{t("register.name")}</label>
                    <input
                      type="text"
                      id="name"
                      className="form-control mb-2"
                      placeholder={t("register.namePlaceholder")}
                      value={user.name}
                      onChange={(e) => setUser({ ...user, name: e.target.value })}
                    />
                  </div>

                  {role === "student" && (
                    <>
                      <div className="col-md-12 col-lg-6">
                        <label htmlFor="Stage">{t("register.stage")}</label>
                        <select
                          id="Stage"
                          className="form-select mb-2"
                          value={user.stage}
                          onChange={(e) => setUser({ ...user, stage: e.target.value })}
                        >
                          <option value="">{t("register.selectStage")}</option>
                          {stages.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-12 col-lg-6">
                        <label htmlFor="grade">{t("register.grade")}</label>
                        <select
                          id="grade"
                          className="form-select mb-2"
                          value={user.grade}
                          onChange={(e) => setUser({ ...user, grade: e.target.value })}
                        >
                          <option value="">{t("register.selectGrade")}</option>
                          {filteredGrades.map((grade) => (
                            <option key={grade.id} value={grade.id}>
                              {grade.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="email">{t("register.email")}</label>
                    <input
                      type="email"
                      id="email"
                      className="form-control mb-2"
                      placeholder={t("register.emailPlaceholder")}
                      value={user.email}
                      onChange={(e) => setUser({ ...user, email: e.target.value })}
                    />
                  </div>

                  {/* Password with eye */}
                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="password">{t("register.password")}</label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        className="form-control mb-2 pe-5"
                        placeholder={t("register.passwordPlaceholder")}
                        value={user.password}
                        onChange={(e) => setUser({ ...user, password: e.target.value })}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                      >
                        <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </span>
                    </div>
                  </div>

                  {/* Confirm password with eye */}
                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="confirm-password">{t("register.confirmPassword")}</label>
                    <div className="position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirm-password"
                        className="form-control mb-2 pe-5"
                        placeholder={t("register.confirmPasswordPlaceholder")}
                        value={user.confirmPassword}
                        onChange={(e) => setUser({ ...user, confirmPassword: e.target.value })}
                      />
                      <span
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                      >
                        <i className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </span>
                    </div>
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="birth">{t("register.birth")}</label>
                    <input
                      type="date"
                      id="birth"
                      className="form-control mb-2"
                      value={user.birth}
                      onChange={(e) => setUser({ ...user, birth: e.target.value })}
                    />
                  </div>

                  {role === "parent" && (
                    <div className="col-md-12 col-lg-6">
                      <label htmlFor="Code">{t("register.childCode")}</label>
                      <input
                        type="text"
                        id="Code"
                        className="form-control mb-2"
                        placeholder={t("register.childCodePlaceholder")}
                        value={user.childCode}
                        onChange={(e) => setUser({ ...user, childCode: e.target.value })}
                      />
                    </div>
                  )}

                  <div className="col-md-12 col-lg-6">
                    <label htmlFor="image">{t("register.image")}</label>
                    <input
                      type="file"
                      id="image"
                      className="form-control mb-2"
                      onChange={handleImageChange}
                    />
                  </div>

                  <div className="d-flex justify-content-center align-items-center my-3">
                    <div>
                      <label>{t("register.student")}</label>
                      <input
                        type="radio"
                        name="role"
                        value="student"
                        checked={role === "student"}
                        onChange={() => setRole("student")}
                        className="ms-1 mx-2"
                      />
                    </div>
                    <div className="mx-4">
                      <label>{t("register.parent")}</label>
                      <input
                        type="radio"
                        name="role"
                        value="parent"
                        checked={role === "parent"}
                        onChange={() => setRole("parent")}
                        className="ms-1 mx-2"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  style={{ backgroundColor: "#352E64", color: "#fff" }}
                  className="btn w-100 py-2 my-2"
                >
                  {t("register.signUp")}
                </button>
              </form>

              <p style={{ color: "#352E64" }} className="text-center mt-3">
                {t("register.haveAccount")} <Link to="/login" className="fw-bold">{t("register.signIn")}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
